<script setup lang="ts">
import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import { useFrameStore } from '../stores/frameStore'

const frameStore = useFrameStore()
const viewportRef = inject<any>('viewportRef')

// Minimap 尺寸（固定，不受字型大小影響）
const MINIMAP_W = 150
const MINIMAP_H = 113
const SCALE = MINIMAP_W / 4000

// 顏色對應
const COLOR_MAP: Record<string, string> = {
  blue: '#93c5fd',
  green: '#86efac',
  orange: '#fdba74',
  purple: '#d8b4fe',
  red: '#fca5a5',
  cyan: '#67e8f9',
}

// minimap 位置（初始在右下角）
const minimapEl = ref<HTMLElement>()
const minimapLeft = ref<number | null>(null)
const minimapTop = ref<number | null>(null)

// viewport 信息
const scrollLeft = ref(0)
const scrollTop = ref(0)
const viewportW = ref(0)
const viewportH = ref(0)

// 拖拽狀態
const isDraggingMinimap = ref(false)
const isDraggingViewport = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const dragStartLeft = ref(0)
const dragStartTop = ref(0)

// 監聽 viewport 捲軸
const updateViewportInfo = () => {
  const el = viewportRef?.value
  if (!el) return
  scrollLeft.value = el.scrollLeft
  scrollTop.value = el.scrollTop
  viewportW.value = el.clientWidth
  viewportH.value = el.clientHeight
}

onMounted(() => {
  const el = viewportRef?.value
  if (!el) return
  updateViewportInfo()
  el.addEventListener('scroll', updateViewportInfo)
  window.addEventListener('resize', updateViewportInfo)
  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('mouseup', handleMouseUp)
})

onUnmounted(() => {
  const el = viewportRef?.value
  if (!el) return
  el.removeEventListener('scroll', updateViewportInfo)
  window.removeEventListener('resize', updateViewportInfo)
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseup', handleMouseUp)
})

// Minimap 位置樣式
const minimapPos = computed(() => {
  if (minimapLeft.value !== null && minimapTop.value !== null) {
    return {
      left: minimapLeft.value + 'px',
      top: minimapTop.value + 'px',
      right: 'auto',
      bottom: 'auto',
    }
  }
  return {
    right: '16px',
    bottom: '16px',
  }
})

// 視口矩形樣式
const viewportRectStyle = computed(() => ({
  left: scrollLeft.value * SCALE + 'px',
  top: scrollTop.value * SCALE + 'px',
  width: viewportW.value * SCALE + 'px',
  height: viewportH.value * SCALE + 'px',
}))

// 點擊 minimap 空白區
const handleMinimapClick = (e: MouseEvent) => {
  const el = viewportRef?.value
  if (!el) return
  const canvas = e.currentTarget as HTMLElement
  const rect = canvas.getBoundingClientRect()
  const clickX = (e.clientX - rect.left) / SCALE
  const clickY = (e.clientY - rect.top) / SCALE

  // 以點擊點為中心捲動
  el.scrollLeft = clickX - viewportW.value / 2
  el.scrollTop = clickY - viewportH.value / 2
}

// 開始拖拽 minimap（頂部欄）
const startDragMinimap = (e: MouseEvent) => {
  isDraggingMinimap.value = true
  dragStartX.value = e.clientX
  dragStartY.value = e.clientY

  // 如果還未切換到 left/top，計算初始位置
  if (minimapEl.value && minimapLeft.value === null && minimapTop.value === null) {
    const rect = minimapEl.value.getBoundingClientRect()
    minimapLeft.value = rect.left
    minimapTop.value = rect.top
  }

  dragStartLeft.value = minimapLeft.value || 0
  dragStartTop.value = minimapTop.value || 0
}

// 開始拖拽視口矩形
const startDragViewport = (e: MouseEvent) => {
  isDraggingViewport.value = true
  dragStartX.value = e.clientX
  dragStartY.value = e.clientY
}

// 滑鼠移動
const handleMouseMove = (e: MouseEvent) => {
  if (isDraggingMinimap.value) {
    const deltaX = e.clientX - dragStartX.value
    const deltaY = e.clientY - dragStartY.value

    // 限制在視窗邊界內
    let newLeft = dragStartLeft.value + deltaX
    let newTop = dragStartTop.value + deltaY

    const minLeft = 0
    const maxLeft = window.innerWidth - MINIMAP_W - 16
    const minTop = 0
    const maxTop = window.innerHeight - (MINIMAP_H + 22) - 16 // 22 = header height

    newLeft = Math.max(minLeft, Math.min(maxLeft, newLeft))
    newTop = Math.max(minTop, Math.min(maxTop, newTop))

    minimapLeft.value = newLeft
    minimapTop.value = newTop
  }

  if (isDraggingViewport.value && viewportRef?.value) {
    const deltaX = e.clientX - dragStartX.value
    const deltaY = e.clientY - dragStartY.value

    viewportRef.value.scrollLeft += deltaX / SCALE
    viewportRef.value.scrollTop += deltaY / SCALE

    dragStartX.value = e.clientX
    dragStartY.value = e.clientY
  }
}

// 滑鼠鬆開
const handleMouseUp = () => {
  isDraggingMinimap.value = false
  isDraggingViewport.value = false
}
</script>

<template>
  <div class="minimap-container" ref="minimapEl" :style="minimapPos">
    <div class="minimap-header" @mousedown.stop="startDragMinimap">
      <span>總覽</span>
      <span>{{ frameStore.frames.length }} 個框</span>
    </div>
    <div class="minimap-canvas" @click="handleMinimapClick">
      <div
        v-for="f in frameStore.frames"
        :key="f.id"
        class="minimap-frame"
        :style="{
          left: f.x * SCALE + 'px',
          top: f.y * SCALE + 'px',
          width: f.w * SCALE + 'px',
          height: f.h * SCALE + 'px',
          backgroundColor: COLOR_MAP[f.color] || '#bfdbfe',
        }"
      />
      <div
        class="minimap-viewport"
        :style="viewportRectStyle"
        @mousedown.stop="startDragViewport"
      />
    </div>
  </div>
</template>

<style scoped>
.minimap-container {
  position: fixed;
  width: 150px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 200;
  user-select: none;
}

.minimap-header {
  height: 22px;
  cursor: grab;
  background: #f3f4f6;
  border-radius: 6px 6px 0 0;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
  border-bottom: 1px solid #e5e7eb;
}

.minimap-header:active {
  cursor: grabbing;
}

.minimap-canvas {
  position: relative;
  width: 150px;
  height: 113px;
  overflow: hidden;
  background: #f9fafb;
  cursor: crosshair;
}

.minimap-frame {
  position: absolute;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 1px;
  pointer-events: none;
}

.minimap-viewport {
  position: absolute;
  border: 2px solid #3b82f6;
  background: rgba(59, 130, 246, 0.08);
  border-radius: 1px;
  cursor: move;
  box-sizing: border-box;
}

.minimap-viewport:hover {
  background: rgba(59, 130, 246, 0.12);
}
</style>
